// Régua de leitura de dinheiro digitado em pt-BR (2026-09-02).
//
// 🚨 Por que isto existe: o `InputNumber` do antd, SEM `decimalSeparator`,
// roda o parser padrão `String(v).replace(/[^\w.-]+/g, '')` — que não ignora
// a vírgula, ele a APAGA. No campo de valor do rateio do Contas a Pagar,
// digitar "10,50" virava 1050 e "0,05" virava 5 (medido com o próprio
// @rc-component/input-number 1.6.2 em 02/09/2026). O rateio nunca fechava com
// o valor do lançamento e a pessoa concluía, com razão, que "o sistema não
// deixa pôr centavos" — a trava de soma do servidor é o que impediu o valor
// errado de ser gravado.
//
// A regra aqui aceita as duas formas que uma pessoa usa de verdade:
//   "10,50"     → 10.50   (vírgula decimal — teclado numérico brasileiro)
//   "10.50"     → 10.50   (ponto decimal — quem digita como o sistema mostrava)
//   "1.234,56"  → 1234.56 (ponto de milhar + vírgula decimal, ex.: colado)
// Sem vírgula, o ponto continua sendo DECIMAL (não milhar): "1.234" é 1,234 —
// ambíguo por natureza, e essa é a leitura que o campo sempre teve.

/** Texto digitado → string numérica que o InputNumber/Number entende ('' se vazio). */
export function parseDinheiroBr(txt: string | undefined | null): string {
  const bruto = String(txt ?? '').trim();
  if (!bruto) return '';
  const negativo = /^-/.test(bruto);
  // Só dígitos, vírgula e ponto interessam (R$, espaço e afins caem fora).
  let s = bruto.replace(/[^\d.,]/g, '');
  const iv = s.lastIndexOf(',');
  const ip = s.lastIndexOf('.');
  if (iv >= 0 && ip >= 0) {
    // Os DOIS separadores no texto: o ÚLTIMO é o decimal, o outro é milhar.
    // Cobre "1.234,56" (pt-BR) e "1,234.56" (colado de planilha em inglês) —
    // este segundo dava 1,23 quando a regra assumia a vírgula como decimal.
    const dec = iv > ip ? ',' : '.';
    const mil = dec === ',' ? '.' : ',';
    s = s.split(mil).join('');
    const pos = s.lastIndexOf(dec);
    s = s.slice(0, pos) + '.' + s.slice(pos + 1);
  } else if (iv >= 0) {
    s = s.split(',').join('.');
  }
  // Sobrou mais de um ponto? Só é milhar se o texto TIVER CARA de milhar
  // (grupos de 3: "1.234.567"). "10,,5" e "1,2,3" chegam aqui como "10..5" e
  // "1.2.3" — devolvê-los "limpos" daria 105 e 123, que é o mesmo tipo de erro
  // silencioso que esta régua existe para matar. Nesse caso devolvemos o texto
  // inválido: o InputNumber descarta a entrada em vez de inventar um valor.
  if ((s.match(/\./g) || []).length > 1) {
    if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
    else return (negativo ? '-' : '') + s;
  }
  // 🚨 "." sozinho é DIGITAÇÃO EM ANDAMENTO, não campo vazio: quem apaga tudo e
  // digita "," ainda vai escrever os centavos. Devolver '' aqui fazia o campo
  // zerar e ",50" acabava virando 50 (revisão adversarial de 02/09/2026).
  if (!s) return '';
  return (negativo ? '-' : '') + s;
}

/** Props do antd InputNumber para campo de dinheiro em pt-BR.
 *  Espalhe em TODO campo de dinheiro: `<InputNumber {...PROPS_DINHEIRO_BR} />`.
 *
 *  O cast do parser é proposital: o antd tipa `parser` como `(v) => number`,
 *  mas quem consome é o rc-input-number, cujo parser PADRÃO devolve string
 *  (`String(v).replace(...)`) — string é o contrato de runtime, e é o que
 *  preserva "10," enquanto a pessoa ainda está digitando. */
export const PROPS_DINHEIRO_BR = {
  decimalSeparator: ',',
  step: 0.01,
  parser: ((v?: string) => parseDinheiroBr(v)) as unknown as (v?: string) => number,
};
